import { STAGE_COLORS } from '@/constants/stages'

/**
 * Utilitários para temas e cores reutilizáveis
 * Centraliza lógica de cores e contrastes para consistência em toda aplicação
 */

// Tipos para temas aprimorados
export interface EnhancedTheme {
  accent: string
  progress: string
  border: string
  hover: string
}

// Tipos para indicadores de status
export interface StatusTheme {
  bg: string
  border: string
  text: string
  icon: string
  label: string
  description: string
}

/**
 * Função para unir classes CSS condicionalmente
 * Wrapper do clsx/cn para uso consistente
 */
export { cn } from '@/lib/utils'

/**
 * Gera tema aprimorado baseado no estágio
 * Utiliza as constantes STAGE_COLORS como base
 */
export const getEnhancedTheme = (stage: string): EnhancedTheme => {
  const stageConfig = STAGE_COLORS[stage as keyof typeof STAGE_COLORS]
  
  if (!stageConfig) {
    return {
      accent: 'bg-slate-700',
      progress: 'bg-slate-200',
      border: 'border-slate-200',
      hover: 'hover:bg-slate-50'
    }
  }

  // Extrai a cor base do background para criar variações
  const colorMatch = stageConfig.bg.match(/(blue|violet|purple|indigo|teal|cyan|emerald|slate)-/)
  const baseColor = colorMatch ? colorMatch[1] : 'slate'
  
  // Mapear cores específicas para melhores contrastes
  const accentColorMap: Record<string, string> = {
    blue: 'bg-blue-700',
    violet: 'bg-violet-800',
    purple: 'bg-purple-800',
    indigo: 'bg-indigo-800',
    teal: 'bg-teal-800',
    cyan: 'bg-cyan-800',
    emerald: 'bg-emerald-700',
    slate: 'bg-slate-700'
  }
  
  return {
    accent: accentColorMap[baseColor] || 'bg-slate-700',
    progress: `bg-${baseColor}-200`,
    border: `border-${baseColor}-200`,
    hover: `hover:bg-${baseColor}-50`
  }
}

/**
 * Temas padronizados para indicadores de status
 * Mantém consistência visual em toda aplicação
 */
export const getStatusThemes = () => ({
  delayed: {
    bg: 'bg-red-50 dark:bg-red-950/40',
    border: 'border-red-200 dark:border-red-800/50',
    text: 'text-red-800 dark:text-red-200',
    icon: 'bg-red-600',
    label: 'text-red-800 dark:text-red-200',
    description: 'text-red-700 dark:text-red-300'
  } as StatusTheme,
  
  onTime: {
    bg: 'bg-green-50 dark:bg-green-950/40',
    border: 'border-green-200 dark:border-green-800/50',
    text: 'text-green-800 dark:text-green-200',
    icon: 'bg-green-600',
    label: 'text-green-800 dark:text-green-200',
    description: 'text-green-700 dark:text-green-300'
  } as StatusTheme,
  
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-800/50',
    text: 'text-amber-800 dark:text-amber-200',
    icon: 'bg-amber-600',
    label: 'text-amber-800 dark:text-amber-200',
    description: 'text-amber-700 dark:text-amber-300'
  } as StatusTheme,
  
  neutral: {
    bg: 'bg-slate-50 dark:bg-slate-950/40',
    border: 'border-slate-200 dark:border-slate-800/50',
    text: 'text-slate-800 dark:text-slate-200',
    icon: 'bg-slate-600',
    label: 'text-slate-800 dark:text-slate-200',
    description: 'text-slate-700 dark:text-slate-300'
  } as StatusTheme
})

/**
 * Classes CSS comuns para componentes
 * Evita repetição e garante consistência
 */
export const commonStyles = {
  // Cards e containers
  card: {
    base: 'bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-slate-200/60 dark:border-slate-700/60 rounded-xl shadow-sm',
    interactive: 'group cursor-pointer transition-all duration-200 hover:shadow-md hover:shadow-slate-200/50 dark:hover:shadow-slate-900/20 hover:-translate-y-0.5 hover:scale-[1.02]',
    loading: 'bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border-slate-200/40 dark:border-slate-700/40 rounded-xl shadow-sm'
  },
  
  // Inputs e formulários
  input: {
    base: 'bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-slate-200/60 dark:border-slate-700/60 rounded-lg',
    focus: 'focus:border-slate-300 dark:focus:border-slate-600 focus:ring-1 focus:ring-slate-200 dark:focus:ring-slate-700',
    transition: 'transition-all duration-200'
  },
  
  // Ícones e botões
  icon: {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    // Versões sem borda para casos específicos (mantidas para compatibilidade)
    smNoBorder: 'h-4 w-4',
    mdNoBorder: 'h-5 w-5',
    lgNoBorder: 'h-6 w-6'
  },
  
  // Contêineres de ícones - onde ficam backgrounds e bordas
  iconContainer: {
    sm: 'p-1.5 rounded-md ring-1 ring-border/20',
    md: 'p-2 rounded-lg ring-1 ring-border/20', 
    lg: 'p-2.5 rounded-lg ring-1 ring-border/20',
    // Versões sem borda para casos específicos
    smNoBorder: 'p-1.5 rounded-md',
    mdNoBorder: 'p-2 rounded-lg',
    lgNoBorder: 'p-2.5 rounded-lg'
  },
  
  // Tipografia
  text: {
    title: 'text-2xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight',
    subtitle: 'text-sm text-slate-600 dark:text-slate-400 font-medium',
    label: 'text-xs font-medium',
    body: 'text-sm'
  }
} 